import type { Node } from 'web-tree-sitter';
import type { LanguageId } from '../pipeline/types';

// Per-language node types considered a "top-level enclosing construct" —
// deliberately conservative: it's better to include a slightly larger
// construct (e.g. the whole exported class) than to cut a snippet that
// doesn't stand on its own.
const CONSTRUCT_TYPES: Record<LanguageId, string[]> = {
  javascript: [
    'function_declaration',
    'class_declaration',
    'method_definition',
    'lexical_declaration',
    'variable_declaration',
    'export_statement',
  ],
  typescript: [
    'function_declaration',
    'class_declaration',
    'method_definition',
    'lexical_declaration',
    'variable_declaration',
    'export_statement',
    'interface_declaration',
    'type_alias_declaration',
  ],
  python: ['function_definition', 'class_definition'],
};

const IMPORT_TYPES: Record<LanguageId, string[]> = {
  javascript: ['import_statement'],
  typescript: ['import_statement'],
  python: ['import_statement', 'import_from_statement'],
};

const CLASS_TYPES: Record<LanguageId, string[]> = {
  javascript: ['class_declaration'],
  typescript: ['class_declaration'],
  python: ['class_definition'],
};

export interface ExtractedConstruct {
  nodeType: string;
  startIndex: number;
  endIndex: number;
  text: string;
  /** Only the import lines whose bound names are actually referenced inside `text`. */
  importLines: string[];
}

/** Walks up from the node at `offset` to the nearest node type considered a top-level construct. */
export function findEnclosingConstruct(root: Node, offset: number, languageId: LanguageId): Node {
  const types = CONSTRUCT_TYPES[languageId];
  let node: Node | null = root.descendantForIndex(offset) ?? root;
  let best: Node | null = null;
  while (node) {
    if (types.includes(node.type)) {
      best = node;
      // An export_statement wraps the "real" declaration — keep climbing past
      // it once so e.g. `export class Foo {}` is captured whole, but stop
      // there rather than climbing indefinitely toward the module root.
      if (node.type !== 'export_statement') break;
    }
    node = node.parent;
  }
  if (!best) return root;

  // A bare method isn't self-contained — it relies on `this`/sibling members
  // for meaning. If the match is method-level, prefer the whole enclosing
  // class instead (and any export wrapping that class).
  const isMethodLevel =
    best.type === 'method_definition' || (languageId === 'python' && best.type === 'function_definition');
  if (isMethodLevel) {
    const classTypes = CLASS_TYPES[languageId];
    let ancestor: Node | null = best.parent;
    while (ancestor) {
      if (classTypes.includes(ancestor.type)) {
        return ancestor.parent?.type === 'export_statement' ? ancestor.parent : ancestor;
      }
      ancestor = ancestor.parent;
    }
  }
  return best;
}

export function extractConstruct(
  root: Node,
  offset: number,
  sourceText: string,
  languageId: LanguageId,
): ExtractedConstruct {
  const node = findEnclosingConstruct(root, offset, languageId);
  const text = sourceText.slice(node.startIndex, node.endIndex);

  const importTypes = IMPORT_TYPES[languageId];
  const importLines: string[] = [];
  for (const child of root.namedChildren) {
    if (!importTypes.includes(child.type)) continue;
    const importText = sourceText.slice(child.startIndex, child.endIndex);
    const boundNames = extractBoundNames(child);
    if (boundNames.some((name) => referencesIdentifier(text, name))) {
      importLines.push(importText);
    }
  }

  return { nodeType: node.type, startIndex: node.startIndex, endIndex: node.endIndex, text, importLines };
}

function referencesIdentifier(text: string, name: string): boolean {
  if (!name) return false;
  return new RegExp(`\\b${escapeRegExp(name)}\\b`).test(text);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Heuristic: every identifier-ish named node inside an import statement is
// treated as a name it binds. Not a real scope resolver, but enough to
// decide "is this import worth keeping" without one.
function extractBoundNames(importNode: Node): string[] {
  const names: string[] = [];
  const walk = (n: Node) => {
    if (n.type === 'identifier' || n.type === 'property_identifier' || n.type === 'dotted_name') {
      names.push(n.text);
    }
    for (const child of n.namedChildren) walk(child);
  };
  walk(importNode);
  return names;
}
